import React, { useState, useEffect, useCallback } from 'react';
import {
  getSectionContent,
  updateSectionContent
} from '../services/contentService';
import { toast } from 'react-toastify';
import ImageUploader from './common/ImageUploader';
import { SketchPicker } from 'react-color';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  Paper,
  Divider,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
} from '@mui/material';
import {
  SettingsOutlined,
  StorefrontOutlined,
  InfoOutlined,
  ArrowForward,
  SaveOutlined
} from '@mui/icons-material';

const ProductManagement = () => {
  const navigate = useNavigate();

  const initialPortfolioPageSettings = {
    mainTitle: "Our Products",
    mainTitleColor: "#1A202C",
    mainTitleFontSize: "medium",
    subtitle: "Browse our selection of high-quality diesel fuel injection parts and turbochargers.",
    subtitleColor: "#4A5568",
    subtitleFontSize: "small",
    sectionBackgroundColor: "#F7FAFC",
    filterBoxBackgroundColor: "#FFFFFF",
    filterBoxTextColor: "#2D3748",
    productPageImageUrl: "",
  };

  const [portfolioPageConfig, setPortfolioPageConfig] = useState(initialPortfolioPageSettings);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const config = await getSectionContent('portfolioSection');
        if (config) {
          const mapFontSizeToMui = (tailwindClass) => {
            if (tailwindClass === 'text-xs') return 'caption';
            if (tailwindClass === 'text-sm') return 'body2';
            if (tailwindClass === 'text-md') return 'body1';
            if (tailwindClass === 'text-lg') return 'h6';
            if (tailwindClass === 'text-xl') return 'h5';
            if (tailwindClass === 'text-2xl') return 'h4';
            if (tailwindClass === 'text-3xl') return 'h3';
            if (tailwindClass === 'text-4xl') return 'h2';
            return 'body1';
          };

          const newConfig = {
            ...initialPortfolioPageSettings,
            ...config,
            mainTitleFontSize: mapFontSizeToMui(config.mainTitleFontSize),
            subtitleFontSize: mapFontSizeToMui(config.subtitleFontSize),
            productPageImageUrl: config.productPageImageUrl || initialPortfolioPageSettings.productPageImageUrl,
          };
          setPortfolioPageConfig(newConfig);
        } else {
          setPortfolioPageConfig(initialPortfolioPageSettings);
        }
      } catch (err) {
        const errorMessage = err.message || 'Failed to fetch initial data for Product Management.';
        setError(errorMessage);
        toast.error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const handlePortfolioConfigChange = (e) => {
    const { name, value } = e.target;
    setPortfolioPageConfig(prev => ({ ...prev, [name]: value }));
  };

  const handlePortfolioConfigColorChange = (name, color) => {
    setPortfolioPageConfig(prev => ({ ...prev, [name]: color.hex }));
  };

  const handleProductPageImageUploaded = useCallback((url) => {
    setPortfolioPageConfig(prev => ({ ...prev, productPageImageUrl: url }));
  }, []);

  const handleUpdatePortfolioConfig = async (e) => {
      e.preventDefault();
      setIsLoading(true);
      try {
          // Clone the config and convert font sizes back to 'text-xl' format if needed for backend
          const configToSave = { ...portfolioPageConfig };

          const mapMuiToTailwindFontSize = (muiVariant) => {
              if (muiVariant === 'caption') return 'text-xs';
              if (muiVariant === 'body2') return 'text-sm';
              if (muiVariant === 'body1') return 'text-md';
              if (muiVariant === 'h6') return 'text-lg';
              if (muiVariant === 'h5') return 'text-xl';
              if (muiVariant === 'h4') return 'text-2xl';
              if (muiVariant === 'h3') return 'text-3xl';
              if (muiVariant === 'h2') return 'text-4xl';
              return 'text-md'; // Default if no match
          };

          configToSave.mainTitleFontSize = mapMuiToTailwindFontSize(portfolioPageConfig.mainTitleFontSize);
          configToSave.subtitleFontSize = mapMuiToTailwindFontSize(portfolioPageConfig.subtitleFontSize);

          await updateSectionContent('portfolioSection', configToSave);
          toast.success('Portfolio page settings updated successfully!');
      } catch (err) {
          setError(err.message || 'Failed to update portfolio page settings.');
          toast.error(err.message || 'Failed to update portfolio page settings.');
      } finally {
          setIsLoading(false);
      }
  };


  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <Box sx={{ maxWidth: '1280px', mx: 'auto' }}>
        {/* Header */}
        <Paper elevation={3} sx={{ p: 4, borderRadius: '12px', mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom
              sx={{ fontWeight: 'bold', background: 'linear-gradient(45deg, #2196f3 30%, #9c27b0 90%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              Product Management Dashboard
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Manage your portfolio settings and product configurations
            </Typography>
          </Box>
          <Box sx={{ width: 56, height: 56, borderRadius: '12px', background: 'linear-gradient(45deg, #2196f3 30%, #9c27b0 90%)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <StorefrontOutlined sx={{ fontSize: 32, color: 'white' }} />
          </Box>
        </Paper>

        {/* Error and Loading States */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {isLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, color: 'info.main' }}>
            <CircularProgress size={20} sx={{ mr: 2 }} />
            <Typography>Loading...</Typography>
          </Box>
        )}

        <Grid container spacing={4}>
          {/* Portfolio Page Settings */}
          <Grid item xs={12} xl={8}>
            <Paper elevation={3} sx={{ borderRadius: '12px', overflow: 'hidden' }}>
              <Box sx={{ background: 'linear-gradient(45deg, #2196f3 30%, #9c27b0 90%)', p: 3, display: 'flex', alignItems: 'center' }}>
                <SettingsOutlined sx={{ fontSize: 24, color: 'white', mr: 2 }} />
                <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', color: 'white' }}>
                  Portfolio Page Settings
                </Typography>
              </Box>

              <Box component="form" onSubmit={handleUpdatePortfolioConfig} sx={{ p: 4, '& > :not(style)': { mb: 4 } }}>
                {/* Main Title Section */}
                <Paper elevation={1} sx={{ p: 3, borderRadius: '8px', borderLeft: '5px solid #2196f3' }}>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#2196f3', mr: 2 }} />
                    Main Title Configuration
                  </Typography>

                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Title Text"
                        name="mainTitle"
                        value={portfolioPageConfig.mainTitle}
                        onChange={handlePortfolioConfigChange}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" gutterBottom sx={{mb: 1}}>Title Color:</Typography>
                      <Box sx={{ width: '220px', '& > div': { width: '100% !important', boxShadow: 'none !important' } }}>
                        <SketchPicker
                          color={portfolioPageConfig.mainTitleColor}
                          onChangeComplete={(color) => handlePortfolioConfigColorChange('mainTitleColor', color)}
                          width="100%"
                          disableAlpha
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Font Size</InputLabel>
                        <Select
                          name="mainTitleFontSize"
                          value={portfolioPageConfig.mainTitleFontSize}
                          label="Font Size"
                          onChange={handlePortfolioConfigChange}
                        >
                          <MenuItem value="h6">Small</MenuItem>
                          <MenuItem value="h5">Medium</MenuItem>
                          <MenuItem value="h4">Large</MenuItem>
                          <MenuItem value="h3">Extra Large</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Paper>

                {/* Subtitle Section */}
                <Paper elevation={1} sx={{ p: 3, borderRadius: '8px', borderLeft: '5px solid #9c27b0' }}>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#9c27b0', mr: 2 }} />
                    Subtitle Configuration
                  </Typography>

                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Subtitle Text"
                        name="subtitle"
                        value={portfolioPageConfig.subtitle}
                        onChange={handlePortfolioConfigChange}
                        multiline
                        rows={3}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" gutterBottom sx={{mb: 1}}>Subtitle Color:</Typography>
                      <Box sx={{ width: '220px', '& > div': { width: '100% !important', boxShadow: 'none !important' } }}>
                        <SketchPicker
                          color={portfolioPageConfig.subtitleColor}
                          onChangeComplete={(color) => handlePortfolioConfigColorChange('subtitleColor', color)}
                          width="100%"
                          disableAlpha
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Font Size</InputLabel>
                        <Select
                          name="subtitleFontSize"
                          value={portfolioPageConfig.subtitleFontSize}
                          label="Font Size"
                          onChange={handlePortfolioConfigChange}
                        >
                          <MenuItem value="caption">Extra Small</MenuItem>
                          <MenuItem value="body2">Small</MenuItem>
                          <MenuItem value="body1">Medium</MenuItem>
                          <MenuItem value="h6">Large</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Paper>

                {/* Background Colors Section */}
                <Paper elevation={1} sx={{ p: 3, borderRadius: '8px', borderLeft: '5px solid #4caf50' }}>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#4caf50', mr: 2 }} />
                    Background & Filter Settings
                  </Typography>

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" gutterBottom sx={{mb: 1}}>Section Background:</Typography>
                      <Box sx={{ width: '200px', '& > div': { width: '100% !important', boxShadow: 'none !important' } }}>
                        <SketchPicker
                          color={portfolioPageConfig.sectionBackgroundColor}
                          onChangeComplete={(color) => handlePortfolioConfigColorChange('sectionBackgroundColor', color)}
                          width="100%"
                          disableAlpha
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" gutterBottom sx={{mb: 1}}>Filter Box Background:</Typography>
                      <Box sx={{ width: '200px', '& > div': { width: '100% !important', boxShadow: 'none !important' } }}>
                        <SketchPicker
                          color={portfolioPageConfig.filterBoxBackgroundColor}
                          onChangeComplete={(color) => handlePortfolioConfigColorChange('filterBoxBackgroundColor', color)}
                          width="100%"
                          disableAlpha
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" gutterBottom sx={{mb: 1}}>Filter Box Text:</Typography>
                      <Box sx={{ width: '200px', '& > div': { width: '100% !important', boxShadow: 'none !important' } }}>
                        <SketchPicker
                          color={portfolioPageConfig.filterBoxTextColor}
                          onChangeComplete={(color) => handlePortfolioConfigColorChange('filterBoxTextColor', color)}
                          width="100%"
                          disableAlpha
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{
                    mt: 3,
                    py: 1.5,
                    borderRadius: '10px',
                    backgroundColor: '#4CAF50',
                    '&:hover': { backgroundColor: '#388E3C' },
                    fontSize: '1rem',
                  }}
                  disabled={isLoading}
                  startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <SaveOutlined />}
                >
                  {isLoading ? 'Updating...' : 'Update Portfolio Settings'}
                </Button>
              </Box>
            </Paper>
          </Grid>

          {/* Product Management Section (Right Panel) */}
          <Grid item xs={12} xl={4}>
            <Paper elevation={3} sx={{ borderRadius: '12px', overflow: 'hidden', height: 'fit-content' }}>
              <Box sx={{ background: 'linear-gradient(45deg, #9c27b0 30%, #ff4081 90%)', p: 3, display: 'flex', alignItems: 'center' }}>
                <StorefrontOutlined sx={{ fontSize: 24, color: 'white', mr: 2 }} />
                <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', color: 'white' }}>
                  Product Management
                </Typography>
              </Box>

              <Box sx={{ p: 4 }}>
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                  <Box sx={{ mx: 'auto', width: 56, height: 56, bgcolor: '#f3e5f5', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                    <StorefrontOutlined sx={{ fontSize: 32, color: '#9c27b0' }} />
                  </Box>
                  <Typography variant="h6" gutterBottom>Comprehensive Management</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Access the complete product management interface to handle all your products, organize categories, and manage brand information in one place.
                  </Typography>
                </Box>

                <Paper variant="outlined" sx={{ p: 2, borderRadius: '8px', mb: 3, bgcolor: '#f8f8f8', borderLeft: '4px solid #9c27b0' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', color: '#9c27b0', mb: 1 }}>
                    <InfoOutlined sx={{ fontSize: 18, mr: 1 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 'medium' }}>Quick Access</Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 3 }}>
                    Click below to access the full product management interface
                  </Typography>
                </Paper>

                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => navigate('/dashboard/all-products')}
                  sx={{
                    background: 'linear-gradient(45deg, #9c27b0 30%, #ff4081 90%)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #7b1fa2 30%, #f50057 90%)',
                      transform: 'scale(1.01)',
                      boxShadow: '0 3px 15px rgba(0,0,0,0.1)',
                    },
                    py: 1.5,
                    borderRadius: '10px',
                    fontSize: '1rem',
                    transition: 'all 0.2s ease-in-out',
                  }}
                  endIcon={<ArrowForward sx={{ fontSize: 20 }} />}
                >
                  View All Products & More
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default ProductManagement;